import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search, Coins } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustUser, setAdjustUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: { q: query, limit: 100 } });
      setRows(r.data.users || []);
    } catch {
      toast.error("Felhasználók betöltése sikertelen");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setQuery(q.trim());
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    if (!adjustUser) return;
    const n = parseInt(amount, 10);
    if (Number.isNaN(n) || n === 0) {
      toast.error("Adj meg nem nulla kredit összeget (+ vagy −)");
      return;
    }
    if (!reason.trim()) {
      toast.error("Indoklás kötelező");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/admin/users/${adjustUser.id}/credits`, { amount: n, reason: reason.trim() });
      toast.success(`Új egyenleg: ${r.data.credits} kredit`);
      setAdjustUser(null);
      setAmount("");
      setReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Módosítás sikertelen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <form onSubmit={submitSearch} className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Email, név vagy referral kód"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white"
          />
        </div>
        <button type="submit" className="px-4 py-2.5 rounded-full bg-electric text-white text-sm">
          Keresés
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-electric animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04] text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Név / email</th>
                <th className="px-4 py-3">Kredit</th>
                <th className="px-4 py-3">Hirdetések</th>
                <th className="px-4 py-3">Referral</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nincs találat.</td>
                </tr>
              ) : (
                rows.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name || "—"}</div>
                      <div className="text-xs text-slate-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{u.credits ?? 0}</td>
                    <td className="px-4 py-3 tabular-nums">{u.car_count ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{u.referral_code || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        data-testid={`admin-user-credit-${u.id}`}
                        onClick={() => setAdjustUser(u)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full glass text-xs hover:bg-white/10"
                      >
                        <Coins className="w-3 h-3" /> Kredit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {adjustUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <form
            onSubmit={submitAdjust}
            className="glass rounded-2xl border border-white/10 p-6 w-full max-w-md space-y-4"
            data-testid="admin-credit-adjust-dialog"
          >
            <h3 className="font-heading text-lg">Kredit módosítás</h3>
            <p className="text-sm text-slate-400">{adjustUser.name || adjustUser.email}</p>
            <label className="block text-xs text-slate-400">
              Összeg (kredit, pl. +50 vagy -20)
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-white"
                required
              />
            </label>
            <label className="block text-xs text-slate-400">
              Indoklás (naplózva)
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-white resize-none"
                required
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setAdjustUser(null)} className="px-4 py-2 text-sm text-slate-400">
                Mégse
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-full bg-electric text-white text-sm disabled:opacity-50">
                {saving ? "…" : "Mentés"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
